import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Loader2, PackageOpen, FolderPlus, Folder, Inbox, MoreVertical, Pencil, Trash2, Share2, Users } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TopNav } from "@/components/TopNav";
import { OrderCard } from "@/components/OrderCard";
import { STATUS_META, ROLE_LABELS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const SUMMARY_CARDS = [
  { key: "total", label: "Total Orders", color: "text-slate-900", ring: "border-slate-200" },
  { key: "healthy", label: "On Track", color: "text-emerald-600", ring: "border-emerald-200" },
  { key: "attention", label: "At Risk", color: "text-amber-600", ring: "border-amber-200" },
  { key: "critical", label: "Critical", color: "text-rose-600", ring: "border-rose-200" },
  { key: "completed", label: "Completed", color: "text-blue-600", ring: "border-blue-200" },
];

const FILTERS = ["all", "draft", "healthy", "attention", "critical", "completed"];
const FOLDER_ROLES = ["manager", "senior_merchandiser", "merchandiser"];
const inputCls = "h-11 w-full rounded-lg border border-slate-200 bg-white px-3 text-base text-slate-800 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100";

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [toDelete, setToDelete] = useState(null);

  // ---- personal folders (organization only) ----
  const showFolders = FOLDER_ROLES.includes(user?.role);
  const [folders, setFolders] = useState([]);
  const [activeFolder, setActiveFolder] = useState("all"); // "all" | "unfiled" | folderId
  const [newFolderOpen, setNewFolderOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [renameTarget, setRenameTarget] = useState(null); // {id, name}
  const [folderToDelete, setFolderToDelete] = useState(null);
  const [moveOrder, setMoveOrder] = useState(null);
  const [moveSelected, setMoveSelected] = useState(null);
  const [shareFolder, setShareFolder] = useState(null);
  const [factoryUsers, setFactoryUsers] = useState([]);
  const [shareSelected, setShareSelected] = useState([]);
  const [savingShare, setSavingShare] = useState(false);

  const canWrite = !["owner", "manager"].includes(user?.role);
  // Folder-role users start with NO selection (no orders auto-loaded at login).
  const [hasSelected, setHasSelected] = useState(!FOLDER_ROLES.includes(user?.role));
  const [creating, setCreating] = useState(false);

  const loadSummary = useCallback(async () => {
    try {
      const { data } = await api.get("/dashboard/summary");
      setSummary(data);
    } catch (e) { /* summary counts are best-effort */ }
  }, []);

  const loadOrders = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/orders", { params: { status: filter } });
      setOrders(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  const loadFolders = useCallback(async () => {
    if (!showFolders) return;
    try {
      const { data } = await api.get("/folders");
      setFolders(data);
    } catch (e) {
      /* folders are optional; ignore */
    }
  }, [showFolders]);

  useEffect(() => { loadSummary(); }, [loadSummary]);
  useEffect(() => { if (hasSelected) loadOrders(); }, [loadOrders, hasSelected]);
  useEffect(() => { loadFolders(); }, [loadFolders]);

  // Selection helpers — folder and status are independent contexts that combine.
  const pickFolder = (id) => { setActiveFolder(id); setHasSelected(true); };
  const pickStatus = (f) => { setFilter(f); setHasSelected(true); };

  // orderId -> folderId (for this user)
  const orderFolderMap = useMemo(() => {
    const m = {};
    folders.forEach((f) => (f.order_ids || []).forEach((oid) => { m[oid] = f.id; }));
    return m;
  }, [folders]);

  const filtered = useMemo(() => {
    let list = orders;
    if (search.trim()) {
      const t = search.toLowerCase();
      list = list.filter((o) =>
        [o.buyer_name, o.style_number, o.department, o.created_by_name,
          ...(o.po_details || []).map((p) => p.po_number)]
          .join(" ").toLowerCase().includes(t)
      );
    }
    if (showFolders && folders.length > 0) {
      if (activeFolder === "unfiled") list = list.filter((o) => !orderFolderMap[o.id]);
      else if (activeFolder !== "all") list = list.filter((o) => orderFolderMap[o.id] === activeFolder);
    }
    return list;
  }, [orders, search, showFolders, folders, activeFolder, orderFolderMap]);

  const handleDuplicate = async (order) => {
    try {
      const { data } = await api.post(`/orders/${order.id}/duplicate`);
      toast.success("Order duplicated as a draft");
      navigate(`/orders/${data.id}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const confirmDelete = async () => {
    try {
      await api.delete(`/orders/${toDelete.id}`);
      toast.success("Order deleted");
      setToDelete(null);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  // ---- folder actions ----
  const createFolder = async () => {
    const name = newFolderName.trim();
    if (!name || creating) return; // guard against duplicate creation from one action
    setCreating(true);
    try {
      await api.post("/folders", { name });
      setNewFolderName("");
      setNewFolderOpen(false);
      toast.success("Folder created");
      loadFolders();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const doRename = async () => {
    const name = (renameTarget?.name || "").trim();
    if (!name) return;
    try {
      await api.put(`/folders/${renameTarget.id}`, { name });
      setRenameTarget(null);
      toast.success("Folder renamed");
      loadFolders();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const doDeleteFolder = async () => {
    try {
      await api.delete(`/folders/${folderToDelete.id}`);
      if (activeFolder === folderToDelete.id) setActiveFolder("all");
      setFolderToDelete(null);
      toast.success("Folder deleted — its orders are now unfiled");
      loadFolders();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const moveTo = async (folderId) => {
    try {
      await api.post(`/folders/${folderId}/orders`, { order_id: moveOrder.id });
      toast.success("Order moved to folder");
      setMoveOrder(null);
      setMoveSelected(null);
      loadFolders();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const removeFromFolder = async () => {
    const fid = orderFolderMap[moveOrder.id];
    if (!fid) { setMoveOrder(null); setMoveSelected(null); return; }
    try {
      await api.delete(`/folders/${fid}/orders/${moveOrder.id}`);
      toast.success("Order removed from folder (now unfiled)");
      setMoveOrder(null);
      setMoveSelected(null);
      loadFolders();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  // ---- folder sharing (senior merchandiser only) ----
  const openShare = async (f) => {
    setShareFolder(f);
    setShareSelected(f.shared_user_ids || []);
    try {
      const { data } = await api.get("/factory-users");
      setFactoryUsers(data.filter((u) => u.id !== user.id));
    } catch (e) {
      setFactoryUsers([]);
    }
  };

  const toggleShareUser = (id) => {
    setShareSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const saveShare = async () => {
    if (savingShare) return;
    setSavingShare(true);
    try {
      await api.put(`/folders/${shareFolder.id}/share`, { user_ids: shareSelected });
      toast.success("Folder sharing updated");
      setShareFolder(null);
      loadFolders();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setSavingShare(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <TopNav search={search} onSearch={setSearch} />

      <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">
              {user?.role === "merchandiser" ? "My Orders" : user?.role === "manager" ? "Team Orders" : "Orders Dashboard"}
            </h1>
            <p className="mt-1 text-lg text-slate-500">
              {ROLE_LABELS[user?.role]}
              {user?.role === "owner" && " · read-only view"}
              {user?.role === "manager" && " · orders from your team"}
            </p>
          </div>
          {canWrite && (
            <Button
              data-testid="new-order-btn"
              onClick={() => navigate("/orders/new")}
              className="h-12 gap-2 bg-blue-600 px-6 text-base font-semibold hover:bg-blue-700"
            >
              <Plus className="h-5 w-5" /> New Order
            </Button>
          )}
        </div>

        {/* Summary cards */}
        <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-5">
          {SUMMARY_CARDS.map((c) => (
            <div key={c.key} data-testid={`summary-${c.key}`} className={cn("rounded-xl border bg-white p-5 shadow-sm", c.ring)}>
              <p className="text-sm font-semibold uppercase tracking-wide text-slate-500">{c.label}</p>
              <p className={cn("mt-2 font-heading text-4xl font-extrabold", c.color)}>{summary[c.key] ?? 0}</p>
            </div>
          ))}
        </div>

        {/* My Folders (personal organization) */}
        {showFolders && (
          <div className="mt-8" data-testid="my-folders-section">
            <div className="mb-3 flex items-center justify-between">
              <h2 className="text-sm font-bold uppercase tracking-wider text-slate-500">My Folders</h2>
              <button
                data-testid="new-folder-btn"
                onClick={() => setNewFolderOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm font-semibold text-blue-700 hover:bg-blue-100"
              >
                <FolderPlus className="h-4 w-4" /> New Folder
              </button>
            </div>
            {folders.length > 0 && (
              <div className="flex flex-wrap gap-2">
                <button
                  data-testid="folder-chip-all"
                  onClick={() => pickFolder("all")}
                  className={cn("rounded-full border px-4 py-2 text-base font-semibold transition-colors",
                    hasSelected && activeFolder === "all" ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50")}
                >
                  All Orders
                </button>
                <button
                  data-testid="folder-chip-unfiled"
                  onClick={() => pickFolder("unfiled")}
                  className={cn("inline-flex items-center gap-2 rounded-full border px-4 py-2 text-base font-semibold transition-colors",
                    hasSelected && activeFolder === "unfiled" ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50")}
                >
                  <Inbox className="h-4 w-4" /> Unfiled
                </button>
                {folders.map((f) => (
                  <div key={f.id} className={cn("inline-flex items-center rounded-full border transition-colors",
                    hasSelected && activeFolder === f.id ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50")}>
                    <button
                      data-testid={`folder-chip-${f.id}`}
                      onClick={() => pickFolder(f.id)}
                      className="inline-flex items-center gap-2 py-2 pl-4 pr-2 text-base font-semibold"
                    >
                      <Folder className="h-4 w-4" /> {f.name}
                      {!f.is_owner && (
                        <span className="text-xs font-normal opacity-80">· {f.owner_name}</span>
                      )}
                      <span className={cn("rounded-full px-1.5 text-xs", hasSelected && activeFolder === f.id ? "bg-white/20" : "bg-slate-100 text-slate-500")}>
                        {(f.order_ids || []).length}
                      </span>
                    </button>
                    {f.is_owner ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button data-testid={`folder-menu-${f.id}`} className="mr-1 flex h-7 w-7 items-center justify-center rounded-full hover:bg-black/10">
                            <MoreVertical className="h-4 w-4" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem data-testid={`folder-rename-${f.id}`} onClick={() => setRenameTarget({ id: f.id, name: f.name })}>
                            <Pencil className="mr-2 h-4 w-4" /> Rename
                          </DropdownMenuItem>
                          {user?.role === "senior_merchandiser" && (
                            <DropdownMenuItem data-testid={`folder-share-${f.id}`} onClick={() => openShare(f)}>
                              <Share2 className="mr-2 h-4 w-4" /> Share
                              {(f.shared_user_ids || []).length > 0 && (
                                <span className="ml-auto text-xs font-semibold text-blue-600">{(f.shared_user_ids || []).length}</span>
                              )}
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem data-testid={`folder-delete-${f.id}`} onClick={() => setFolderToDelete(f)} className="text-rose-600 focus:text-rose-600">
                            <Trash2 className="mr-2 h-4 w-4" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : (
                      <span data-testid={`folder-shared-badge-${f.id}`} title={`Shared by ${f.owner_name}`}
                        className="mr-2 flex h-7 w-7 items-center justify-center">
                        <Users className="h-4 w-4 opacity-70" />
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Status filters (unchanged) */}
        <div className="mt-8 flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f}
              data-testid={`filter-${f}`}
              onClick={() => pickStatus(f)}
              className={cn(
                "flex items-center gap-2 rounded-full border px-4 py-2 text-base font-semibold capitalize transition-colors",
                hasSelected && filter === f ? "border-blue-600 bg-blue-600 text-white" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
              )}
            >
              {f !== "all" && <span className={cn("h-2.5 w-2.5 rounded-full", STATUS_META[f]?.dot)} />}
              {f === "all" ? "All Orders" : STATUS_META[f]?.label || f}
            </button>
          ))}
        </div>

        {/* Orders grid */}
        {showFolders && !hasSelected ? (
          <div className="mt-10 flex flex-col items-center rounded-xl border border-dashed border-slate-300 bg-white py-20 text-center" data-testid="select-prompt">
            <PackageOpen className="h-12 w-12 text-slate-300" />
            <p className="mt-4 text-xl font-semibold text-slate-600">Select a folder or status to view orders</p>
            <p className="mt-1 text-base text-slate-400">Pick one of your folders, Unfiled, or a status above to load its orders.</p>
          </div>
        ) : loading ? (
          <div className="flex justify-center py-24">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="mt-10 flex flex-col items-center rounded-xl border border-dashed border-slate-300 bg-white py-20 text-center">
            <PackageOpen className="h-12 w-12 text-slate-300" />
            <p className="mt-4 text-xl font-semibold text-slate-600">No orders found</p>
            <p className="mt-1 text-base text-slate-400">
              {showFolders && folders.length > 0 && activeFolder !== "all"
                ? "No orders in this view."
                : canWrite ? "Create your first order to get started." : "There are no orders to display."}
            </p>
            {canWrite && (!showFolders || folders.length === 0 || activeFolder === "all") && (
              <Button data-testid="empty-new-order-btn" onClick={() => navigate("/orders/new")} className="mt-6 h-12 gap-2 bg-blue-600 px-6 hover:bg-blue-700">
                <Plus className="h-5 w-5" /> New Order
              </Button>
            )}
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((o) => (
              <OrderCard
                key={o.id}
                order={o}
                canWrite={canWrite}
                onEdit={(order) => navigate(`/orders/${order.id}`)}
                onDuplicate={handleDuplicate}
                onDelete={setToDelete}
                onMoveToFolder={showFolders ? setMoveOrder : undefined}
              />
            ))}
          </div>
        )}
      </main>

      {/* Delete order dialog (unchanged) */}
      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-order-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this order?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently remove <strong>{toDelete?.buyer_name} · Style {toDelete?.style_number}</strong>. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="delete-confirm-btn" onClick={confirmDelete} className="bg-rose-600 hover:bg-rose-700">
              Delete order
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* New folder dialog */}
      <Dialog open={newFolderOpen} onOpenChange={(o) => { if (!o) { setNewFolderOpen(false); setNewFolderName(""); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-xl font-bold">New Folder</DialogTitle></DialogHeader>
          <input data-testid="new-folder-input" autoFocus className={inputCls} value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createFolder()} placeholder="e.g. Zara Orders" />
          <DialogFooter>
            <Button variant="outline" className="h-11 px-6" onClick={() => { setNewFolderOpen(false); setNewFolderName(""); }}>Cancel</Button>
            <Button data-testid="new-folder-save-btn" disabled={creating} className="h-11 bg-blue-600 px-6 hover:bg-blue-700" onClick={createFolder}>Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Rename folder dialog */}
      <Dialog open={!!renameTarget} onOpenChange={(o) => !o && setRenameTarget(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-xl font-bold">Rename Folder</DialogTitle></DialogHeader>
          <input data-testid="rename-folder-input" autoFocus className={inputCls} value={renameTarget?.name || ""}
            onChange={(e) => setRenameTarget((t) => ({ ...t, name: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && doRename()} />
          <DialogFooter>
            <Button variant="outline" className="h-11 px-6" onClick={() => setRenameTarget(null)}>Cancel</Button>
            <Button data-testid="rename-folder-save-btn" className="h-11 bg-blue-600 px-6 hover:bg-blue-700" onClick={doRename}>Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete folder dialog */}
      <AlertDialog open={!!folderToDelete} onOpenChange={(o) => !o && setFolderToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete folder “{folderToDelete?.name}”?</AlertDialogTitle>
            <AlertDialogDescription>The orders inside will not be deleted — they simply become unfiled.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="folder-delete-confirm-btn" onClick={doDeleteFolder} className="bg-rose-600 hover:bg-rose-700">Delete folder</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Move order to folder dialog */}
      <Dialog open={!!moveOrder} onOpenChange={(o) => { if (!o) { setMoveOrder(null); setMoveSelected(null); } }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle className="font-heading text-xl font-bold">Move to folder</DialogTitle></DialogHeader>
          <div className="max-h-72 space-y-2 overflow-y-auto py-1">
            {folders.length === 0 && <p className="text-base text-slate-500">You have no folders yet. Create one first.</p>}
            {folders.map((f) => {
              const current = moveOrder && orderFolderMap[moveOrder.id] === f.id;
              const selected = moveSelected === f.id;
              return (
                <button key={f.id} data-testid={`move-to-${f.id}`} onClick={() => setMoveSelected(f.id)}
                  className={cn("flex w-full items-center gap-2 rounded-lg border-2 px-4 py-3 text-left text-base font-semibold transition-colors",
                    selected ? "border-blue-600 bg-blue-50 text-blue-700" : "border-slate-200 hover:bg-slate-50")}>
                  <Folder className="h-4 w-4" /> {f.name}
                  {selected && <span className="ml-auto text-sm font-bold text-blue-600">Selected</span>}
                  {!selected && current && <span className="ml-auto text-sm font-normal text-slate-400">Current</span>}
                </button>
              );
            })}
          </div>
          <DialogFooter>
            {moveOrder && orderFolderMap[moveOrder.id] && (
              <Button variant="outline" data-testid="move-remove-btn" className="h-11 px-5" onClick={removeFromFolder}>Remove from folder</Button>
            )}
            <Button variant="outline" className="h-11 px-6" onClick={() => { setMoveOrder(null); setMoveSelected(null); }}>Cancel</Button>
            <Button
              data-testid="move-confirm-btn"
              className="h-11 bg-blue-600 px-6 hover:bg-blue-700"
              disabled={!moveSelected || (moveOrder && orderFolderMap[moveOrder.id] === moveSelected)}
              onClick={() => moveTo(moveSelected)}
            >
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Share folder dialog (senior merchandiser only) */}
      <Dialog open={!!shareFolder} onOpenChange={(o) => { if (!o) { setShareFolder(null); setShareSelected([]); } }}>
        <DialogContent className="max-w-md" data-testid="share-folder-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold">Share “{shareFolder?.name}”</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-slate-500">
            Selected teammates can view and edit the orders inside this folder. Removing someone never deletes the orders.
          </p>
          <div className="max-h-72 space-y-2 overflow-y-auto py-1">
            {factoryUsers.length === 0 && <p className="text-base text-slate-500">No other teammates in your factory to share with.</p>}
            {factoryUsers.map((u) => {
              const selected = shareSelected.includes(u.id);
              return (
                <button key={u.id} data-testid={`share-user-${u.id}`} onClick={() => toggleShareUser(u.id)}
                  className={cn("flex w-full items-center gap-3 rounded-lg border-2 px-4 py-3 text-left transition-colors",
                    selected ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:bg-slate-50")}>
                  <span className={cn("flex h-5 w-5 shrink-0 items-center justify-center rounded border-2",
                    selected ? "border-blue-600 bg-blue-600 text-white" : "border-slate-300")}>
                    {selected && "✓"}
                  </span>
                  <span className="min-w-0">
                    <span className="block truncate text-base font-semibold text-slate-800">{u.full_name || u.email}</span>
                    <span className="block truncate text-sm text-slate-400">{ROLE_LABELS[u.role] || u.role} · {u.email}</span>
                  </span>
                </button>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" className="h-11 px-6" onClick={() => { setShareFolder(null); setShareSelected([]); }}>Cancel</Button>
            <Button data-testid="share-save-btn" disabled={savingShare} className="h-11 bg-blue-600 px-6 hover:bg-blue-700" onClick={saveShare}>
              Save sharing
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
